import React from 'react';
import Downshift from 'downshift';
import classNames from 'classnames';

import { Icon } from '../Icon/Icon';
import { Loader } from '../Loader/Loader';

interface IProps {
  fieldClassName?: string;
  id: string;
  items: string[];
  label: string;
  noResultsMessage?: JSX.Element,
  onSelect: (value: string) => void;
  placeholder: string;
  selectedItem: string;
  showNoResultsMessageAlways?: boolean;
  onInputValueChange: (inputValue: string) => void;
  isLoading: boolean;
  error?: string | boolean;
  clearLabel: string;
}

export const AutoComplete = (props: IProps) => {
  const {
    fieldClassName = '',
    showNoResultsMessageAlways,
    selectedItem,
    id,
    items,
    label,
    noResultsMessage,
    placeholder,
    onSelect,
    onInputValueChange,
    error,
    clearLabel,
    isLoading = false,
  } = props;

  const handleInputValueChange = (inputValue: string) => {
    onInputValueChange(inputValue);
  };

  const handleSelect = (selectVal: string) => {
    onSelect(selectVal);
  };

  return (
    <Downshift
      onSelect={(val) => handleSelect(val || '')}
      selectedItem={selectedItem}
      stateReducer={(state, changes) => {
        switch (changes.type) {
          case Downshift.stateChangeTypes.changeInput:
            // Don't call handleInputValueChange if the inputValue and selectedItem are the same
            if (state.inputValue === state.selectedItem) {
              return changes;
            }

            // Call handleInputValueChange to trigger any lookup or async work
            if (state.inputValue) {
              handleInputValueChange(state.inputValue);
            }

            return changes;
          default:
            return changes;
        }
      }}
    >
      {({
        getInputProps, getItemProps, getLabelProps, isOpen, highlightedIndex,
      }) => (
        <div className={classNames('AutoComplete', fieldClassName, { hasError: !!error })}>
          <label htmlFor={id} {...getLabelProps()}>
            <span className="AutoComplete-label">{label}</span>
          </label>

          <div className="AutoComplete-container">
            <div className="AutoComplete-icon">
              <Icon name="ic-search" />
            </div>

            <input
              autoComplete="nope"
              // eslint-disable-next-line jsx-a11y/no-interactive-element-to-noninteractive-role
              role="presentation"
              className="AutoComplete-input"
              {...getInputProps({
                id,
                placeholder,
              })}
            />

            {!isLoading && selectedItem && (
              <button title={clearLabel} className="AutoComplete-clearIcon" type="button" onClick={() => handleSelect('')}>
                <Icon name="ic-close" />
                <span className="u-hiddenVisually">
                  {clearLabel}
                </span>
              </button>
            )}

            {isLoading && (
              <div className="AutoComplete-loadingIcon">
                <Loader label={`${label} is loading`} />
              </div>
            )}

            {isOpen && (
              <ul className="AutoComplete-resultList">
                {items.length > 0 && (
                  <>
                    {items.map((item, index) => (
                      <li
                        className="AutoComplete-resultList-item"
                        {...getItemProps({
                          key: item,
                          index,
                          item,
                          style: {
                            backgroundColor: highlightedIndex === index ? '#30d7e5' : '#ffffff',
                          },
                        })}
                      >
                        {item}
                      </li>
                    ))}
                  </>
                )}

                {!isLoading && !items.length && !showNoResultsMessageAlways && noResultsMessage && (
                  <li className="AutoComplete-noResults">
                    {noResultsMessage}
                  </li>
                )}

                {showNoResultsMessageAlways && noResultsMessage && (
                  <li className="AutoComplete-noResults">
                    <p className="u-mV-0">{noResultsMessage}</p>
                  </li>
                )}
              </ul>
            )}

            {error && (
              <span className="AutoComplete-error">
                <Icon name="ic-exclamation" />{error}
              </span>
            )}
          </div>

        </div>
      )}
    </Downshift>
  );
};
